import { z } from "zod";
import {
  dataTypeSchema,
  endianSchema,
  roleSchema,
  transferModeSchema,
  type Timestamp,
} from "./common.js";

export interface MessageFieldMetadata {
  source: string;
  official: boolean;
  verificationStatus: string;
  notes: string;
  validationSource?: "POC" | "PUBLIC_REFERENCE" | "OFFICIAL";
}

export const messageFieldMetadataSchema = z.object({
  source: z.string(),
  official: z.boolean(),
  verificationStatus: z.string(),
  notes: z.string(),
  validationSource: z.enum(["POC", "PUBLIC_REFERENCE", "OFFICIAL"]).optional(),
});

export interface MessageFieldValidationRule {
  type: string;
  params?: Record<string, unknown>;
  message?: string;
}

export const messageFieldValidationRuleSchema = z.object({
  type: z.string(),
  params: z.record(z.unknown()).optional(),
  message: z.string().optional(),
});

export interface MessageFieldConversion {
  storeUnit?: string;
  displayUnit?: string;
  toDisplay?: string;
  toStore?: string;
}

export const messageFieldConversionSchema = z.object({
  storeUnit: z.string().optional(),
  displayUnit: z.string().optional(),
  toDisplay: z.string().optional(),
  toStore: z.string().optional(),
});

export interface MessageFieldGisMapping {
  enabled: boolean;
  property?: string;
}

export const messageFieldGisMappingSchema = z.object({
  enabled: z.boolean(),
  property: z.string().optional(),
});

export interface MessageFieldDefinition {
  [key: string]: unknown;
  order: number;
  key: string;
  name: string;
  displayName: string;
  description?: string;
  dataType: string;
  byteLength?: number;
  required: boolean;
  unit?: string;
  displayUnit?: string;
  minimum?: number;
  maximum?: number;
  defaultValue?: unknown;
  demoValue?: unknown;
  enumRef?: string;
  scale?: number;
  precision?: number;
  endian?: string;
  uiControl?: string;
  gisMapping?: MessageFieldGisMapping;
  conversion?: MessageFieldConversion;
  validationRules?: MessageFieldValidationRule[];
}

export const messageFieldDefinitionSchema: z.ZodSchema<MessageFieldDefinition> =
  z
    .object({
      order: z.number().int(),
      key: z.string(),
      name: z.string(),
      displayName: z.string(),
      description: z.string().optional(),
      dataType: dataTypeSchema,
      byteLength: z.number().int().optional(),
      required: z.boolean(),
      unit: z.string().optional(),
      displayUnit: z.string().optional(),
      minimum: z.number().optional(),
      maximum: z.number().optional(),
      defaultValue: z.unknown().optional(),
      demoValue: z.unknown().optional(),
      enumRef: z.string().optional(),
      scale: z.number().optional(),
      precision: z.number().optional(),
      endian: endianSchema.optional(),
      uiControl: z.string().optional(),
      gisMapping: messageFieldGisMappingSchema.optional(),
      conversion: messageFieldConversionSchema.optional(),
      validationRules: z.array(messageFieldValidationRuleSchema).optional(),
    })
    .passthrough();

export interface MessageDefinition {
  [key: string]: unknown;
  id: string;
  standard: string;
  edition: string;
  messageType: number;
  name: string;
  displayName: string;
  category: string;
  description?: string;
  sourceRole: string;
  targetRole: string;
  transferMode: string;
  defaultPeriodMs?: number;
  acknowledgementRequired: boolean;
  supported: boolean;
  dictionaryVersion?: string;
  fields: MessageFieldDefinition[];
  tags: string[];
  metadata: MessageFieldMetadata;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export const messageDefinitionSchema: z.ZodSchema<MessageDefinition> = z
  .object({
    id: z.string(),
    standard: z.string(),
    edition: z.string(),
    messageType: z.number().int(),
    name: z.string(),
    displayName: z.string(),
    category: z.string(),
    description: z.string().optional(),
    sourceRole: roleSchema,
    targetRole: roleSchema,
    transferMode: transferModeSchema,
    defaultPeriodMs: z.number().int().optional(),
    acknowledgementRequired: z.boolean(),
    supported: z.boolean(),
    dictionaryVersion: z.string().optional(),
    fields: z.array(messageFieldDefinitionSchema),
    tags: z.array(z.string()),
    metadata: messageFieldMetadataSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .passthrough();

export interface MessageInstance {
  id: string;
  messageType: number;
  standard: string;
  name: string;
  source: string;
  target: string;
  direction: string;
  timestamp: Timestamp;
  fields: Record<string, unknown>;
  rawBytes?: number[];
  validation?: string;
  projectId?: string;
  environment?: string;
  createdAt?: string;
  sequenceNumber?: number;
  runId?: string;
  messageDefinitionId?: string;
}

export const messageInstanceSchema = z
  .object({
    id: z.string(),
    messageType: z.number().int(),
    standard: z.string(),
    name: z.string(),
    source: z.string(),
    target: z.string(),
    direction: z.string(),
    timestamp: z.string().datetime(),
    fields: z.record(z.unknown()),
    rawBytes: z.array(z.number()).optional(),
    validation: z.string().optional(),
    projectId: z.string().optional(),
    environment: z.string().optional(),
    createdAt: z.string().datetime().optional(),
    sequenceNumber: z.number().int().optional(),
    runId: z.string().optional(),
    messageDefinitionId: z.string().optional(),
  })
  .passthrough();

export interface ValidationIssue {
  severity: string;
  code: string;
  message: string;
  path?: string;
  scope?: string;
}

export const validationIssueSchema = z.object({
  severity: z.string(),
  code: z.string(),
  message: z.string(),
  path: z.string().optional(),
  scope: z.string().optional(),
});

export interface ValidationResult {
  severity: string;
  issues: ValidationIssue[];
  timestamp: Timestamp;
}

export const validationResultSchema = z.object({
  severity: z.string(),
  issues: z.array(validationIssueSchema),
  timestamp: z.string().datetime(),
});
